"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border border-white rounded-md faq-accordion", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

interface PlusMinusIconProps {
  className?: string;
}

const PlusMinusIcon: React.FC<PlusMinusIconProps> = ({ className }) => {
  return (
    <div
      className={`
        ${className === "bg-[#003350]" ? "bg-[#fff]" : "bg-[#003350]"} 
        w-[42px] h-[42px] min-w-[42px] 
        mobileMax:min-w-[32px] mobileMax:w-[32px] mobileMax:h-[32px] 
        flex items-center justify-center transition-all duration-300
      `}
    >
      <span className="relative flex items-center justify-center h-5 w-5 shrink-0">
        {/* vertical line */}
        <span
          className={`${className}
          absolute h-6 w-[4px] mobileMax:h-5 mobileMax:w-[3.1px] rounded-full transition-all duration-300
          group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0
        `}
        />
        {/* horizontal line */}
        <span
          className={`${className}
          absolute w-6 h-[4px] mobileMax:w-5 mobileMax:h-[3.1px] bg-[#003350] rounded-full transition-all duration-300
        `}
        />
      </span>
    </div>
  );
};

const AccordionTrigger = React.forwardRef<
  React.ElementRef<any>,
  React.ComponentPropsWithoutRef<any>
>(({ className, children, noArrow, plusArrow, bgColor, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex w-full">
    <AccordionPrimitive.Trigger
      ref={ref as any}
      className={cn(
        `group flex flex-1 items-center justify-between py-4 font-medium transition-all gap-[8px]
         ${plusArrow && noArrow && "justify-start"}`,
        className,
      )}
      {...props}
    >
      {plusArrow && noArrow && <PlusMinusIcon className={bgColor} />}

      {children}

      {/* fallback chevron arrow */}
      {!noArrow && (
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      )}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-small leading-6 transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0 accordion-row", className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
